'use memo'

import { useEffect, useRef } from 'react'
import {
  findTeamCurrentOfficial,
  type TeamHistoryFields,
} from '@sportninja/common/features/registration/teamHistory'
import { teamOptionValue, type TeamValueRef } from '../utils/teamOptionValue'

interface Params<T> {
  /** Only a team rep picks a team official. */
  enabled: boolean
  /** The held team; a team from the rep's own list carries `team_official`. */
  selectedTeam: (TeamValueRef & Pick<TeamHistoryFields, 'team_official'>) | null
  /** The reader's team officials. Null while loading. */
  officials: T[] | null | undefined
  onSelect: (official: T) => void
}

/**
 * Pre-select the team's current official in the identity dropdown when a team
 * is chosen.
 *
 * Runs once per team: a later manual change of official is left alone until
 * the team itself changes. Waits for the officials list when the team is
 * chosen first (a single-team auto-select or a restored session). When the
 * team's official is not in the list, the current selection is kept.
 */
export const usePreselectedTeamOfficial = <T extends { id?: string | null }>({
  enabled,
  selectedTeam,
  officials,
  onSelect,
}: Params<T>): void => {
  const appliedTeamKey = useRef<string | undefined>(undefined)
  const teamKey = teamOptionValue(selectedTeam)

  useEffect(() => {
    if (!teamKey) {
      // Cleared: choosing the same team again pre-selects again.
      appliedTeamKey.current = undefined
      return
    }
    if (!enabled || !officials || appliedTeamKey.current === teamKey) {
      return
    }
    appliedTeamKey.current = teamKey
    const official = findTeamCurrentOfficial(selectedTeam, officials)
    if (official) {
      onSelect(official)
    }
  }, [enabled, teamKey, selectedTeam, officials, onSelect])
}
